import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Tooltip,
  Grid,
  Paper,
} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import { API_URL } from "../config";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ImageIcon from "@mui/icons-material/Image";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import QuizIcon from "@mui/icons-material/Quiz";


// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    secondary: { main: "#C4922A", light: "#FEF3C7", dark: "#92680C" },
    error: { main: "#DC2626", light: "#FEE2E2" },
    success: { main: "#059669", light: "#D1FAE5" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    h2: { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const cardEntrance = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;


// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg, #EEF2FF 0%, #F0F2F8 40%, #E8EDF8 100%)",
  padding: "28px 20px 60px",
  fontFamily: "'Sora', sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -120,
    right: -120,
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 1060, margin: "0 auto" });

const SectionCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "delay",
})(({ theme, delay }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
  padding: "24px 28px",
  marginBottom: 20,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(37,99,235,0.06), 0 12px 32px rgba(0,0,0,0.06)",
  },
}));

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.9px",
  color: theme.palette.text.disabled,
  marginBottom: 18,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::before": {
    content: '""',
    display: "inline-block",
    width: 3,
    height: 14,
    background: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));

const PrimaryBtn = styled(Button)(() => ({
  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
  color: "#fff",
  borderRadius: 10,
  padding: "11px 28px",
  fontSize: 14,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Sora', sans-serif",
  boxShadow: "0 4px 14px rgba(37,99,235,0.28)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #1D4ED8 0%, #1E3A8A 100%)",
    boxShadow: "0 6px 20px rgba(37,99,235,0.38)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": {
    background: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none",
  },
}));

const StyledInput = styled("input")(() => ({
  width: "100%",
  fontFamily: "'Sora', sans-serif",
  fontSize: 14,
  color: "#0F1117",
  background: alpha("#F0F2F8", 0.8),
  border: `1px solid ${alpha("#000", 0.1)}`,
  borderRadius: 10,
  padding: "11px 14px",
  outline: "none",
  boxSizing: "border-box",
  transition: "all 0.2s ease",
  "&:focus": {
    borderColor: "#2563EB",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
  },
  "&::placeholder": { color: "#9CA3AF" },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  color: "#9CA3AF",
  marginBottom: 6,
  fontFamily: "'Sora', sans-serif",
}));

const ToggleBox = styled(Box, {
  shouldForwardProp: (p) => p !== "active",
})(({ theme, active }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 18px",
  borderRadius: 12,
  cursor: "pointer",
  border: `1.5px solid ${
    active ? alpha(theme.palette.secondary.main, 0.45) : alpha("#000", 0.08)
  }`,
  background: active
    ? alpha(theme.palette.secondary.main, 0.05)
    : alpha("#F0F2F8", 0.6),
  transition: "all 0.2s ease",
  userSelect: "none",
  "&:hover": {
    borderColor: active
      ? theme.palette.secondary.main
      : alpha(theme.palette.primary.main, 0.3),
    background: active
      ? alpha(theme.palette.secondary.main, 0.08)
      : alpha(theme.palette.primary.main, 0.03),
  },
}));

const ToggleKnob = styled(Box, {
  shouldForwardProp: (p) => p !== "active",
})(({ theme, active }) => ({
  width: 44,
  height: 24,
  borderRadius: 12,
  position: "relative",
  background: active
    ? `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`
    : alpha("#000", 0.15),
  transition: "all 0.22s ease",
  boxShadow: active
    ? `0 2px 8px ${alpha(theme.palette.secondary.main, 0.4)}`
    : "none",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 3,
    left: active ? 23 : 3,
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: "#fff",
    transition: "left 0.22s ease",
    boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
  },
}));

const ExamCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "itemdelay",
})(({ theme, itemdelay }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha("#000", 0.07)}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  animation: `${cardEntrance} 0.45s ease both`,
  animationDelay: `${itemdelay ?? 0}ms`,
  transition: "all 0.22s ease",
  "&:hover": {
    boxShadow: "0 6px 20px rgba(37,99,235,0.09), 0 10px 32px rgba(0,0,0,0.07)",
    transform: "translateY(-2px)",
    borderColor: alpha("#2563EB", 0.2),
  },
}));

const ExamImageBox = styled(Box)(({ theme }) => ({
  height: 130,
  overflow: "hidden",
  position: "relative",
  background: `linear-gradient(135deg, ${alpha("#2563EB", 0.08)} 0%, ${alpha(
    "#1E40AF",
    0.12
  )} 100%)`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const NavActionBtn = styled(Box, {
  shouldForwardProp: (p) => p !== "btnvariant",
})(({ theme, btnvariant }) => {
  const map = {
    objective: { color: "#2563EB", bg: alpha("#2563EB", 0.07), hbg: "#2563EB" },
    theory: { color: "#059669", bg: alpha("#059669", 0.07), hbg: "#059669" },
    edit: { color: "#C4922A", bg: alpha("#C4922A", 0.08), hbg: "#C4922A" },
  };
  const s = map[btnvariant] || map.objective;
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    cursor: "pointer",
    padding: "7px 10px",
    borderRadius: 9,
    border: `1px solid ${alpha(s.color, 0.2)}`,
    background: s.bg,
    flex: 1,
    transition: "all 0.18s ease",
    "&:hover": {
      background: s.hbg,
      borderColor: s.hbg,
      "& .btn-label": { color: "#fff" },
      "& .btn-icon": { color: "#fff" },
      transform: "translateY(-1px)",
      boxShadow: `0 4px 10px ${alpha(s.hbg, 0.28)}`,
    },
  };
});

const Spinner = styled(Box)(() => ({
  width: 32,
  height: 32,
  border: "3px solid rgba(37,99,235,0.15)",
  borderTop: "3px solid #2563EB",
  borderRadius: "50%",
  animation: `${spin} 0.7s linear infinite`,
}));

const ImageUploadZone = styled(Box, {
  shouldForwardProp: (p) => p !== "hasimage",
})(({ theme, hasimage }) => ({
  border: `2px dashed ${
    hasimage ? alpha("#059669", 0.4) : alpha(theme.palette.primary.main, 0.22)
  }`,
  borderRadius: 12,
  padding: "20px 16px",
  textAlign: "center",
  cursor: "pointer",
  background: hasimage ? alpha("#059669", 0.03) : alpha("#F0F2F8", 0.6),
  transition: "all 0.25s ease",
  "&:hover": {
    borderColor: hasimage ? "#059669" : theme.palette.primary.main,
    background: hasimage
      ? alpha("#059669", 0.06)
      : alpha(theme.palette.primary.main, 0.03),
  },
}));

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminExamPage() {
  const { countryId } = useParams();
  const navigate = useNavigate();

  const [exams, setExams] = useState([]);
  const [name, setName] = useState("");
  const [isPostUTME, setIsPostUTME] = useState(false);
  const [examImage, setExamImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editingExamId, setEditingExamId] = useState(null);
  const [fetching, setFetching] = useState(true);

  const fetchExams = useCallback(() => {
    setFetching(true);
    fetch(`${API_URL}/admin/exams/${countryId}/`)
      .then((res) => res.json())
      .then((data) => {
        setExams(
          data.map((exam) => ({
            ...exam,
            country: { name: exam["country__name"] },
          }))
        );
        setFetching(false);
      })
      .catch(() => {
        alert("Failed to fetch exams.");
        setFetching(false);
      });
  }, [countryId]);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  const resetForm = () => {
    setName("");
    setIsPostUTME(false);
    setExamImage(null);
    setImagePreview(null);
    setExistingImage(null);
    setEditingExamId(null);
  };

  const submitExam = async () => {
    if (!name.trim()) {
      alert("Exam name is required.");
      return;
    }
    setIsLoading(true);
    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("is_postUTME", isPostUTME ? "true" : "false");
    console.log("isPostUTME state:", isPostUTME);
    if (examImage) formData.append("exam_img", examImage);

    const method = editingExamId ? "PUT" : "POST";
    const url = editingExamId
      ? `${API_URL}/admin/exam/${editingExamId}/`
      : `${API_URL}/admin/exams/${countryId}/`;

    try {
      const res = await fetch(url, { method, body: formData });
      const data = await res.json();
      if (editingExamId)
        setExams((prev) =>
          prev.map((e) => (e.id === editingExamId ? data : e))
        );
      else setExams((prev) => [data, ...prev]);
      resetForm();
    } catch {
      alert("Failed to save exam.");
    } finally {
      setIsLoading(false);
    }
  };

  const editExam = (examId) => {
    const exam = exams.find((e) => e.id === examId);
    if (!exam) return;
    setName(exam.name);
    setIsPostUTME(exam.is_postUTME);
    setExistingImage(exam.exam_img || null);
    setExamImage(null);
    setImagePreview(null);
    setEditingExamId(examId);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setExamImage(file);
      setImagePreview(URL.createObjectURL(file));
      setExistingImage(null);
    }
  };

  const navigateToSubject = (examId) => navigate(`/admin-subject/${examId}`);
  const navigateToTheory = (examId) =>
    navigate(`/admin-theory-subject/${examId}`);

  const displayImage = imagePreview || existingImage;

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        <ContentBox>
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
              animation: `${fadeUp} 0.4s ease both`,
              flexWrap: "wrap",
            }}
          >
            <BackBtn
              startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(-1)}
            >
              Back
            </BackBtn>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 22, sm: 27 },
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                {editingExamId ? "Edit Exam" : "Manage Exams"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  mt: 0.3,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Country ID:{" "}
                <strong style={{ color: "#4B5563" }}>{countryId}</strong>
              </Typography>
            </Box>
            <Chip
              label={editingExamId ? "Editing" : "New"}
              size="small"
              sx={{
                ml: "auto",
                fontFamily: "'Sora', sans-serif",
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                background: editingExamId
                  ? alpha("#C4922A", 0.12)
                  : alpha("#2563EB", 0.1),
                color: editingExamId ? "#C4922A" : "#2563EB",
                border: `1px solid ${
                  editingExamId ? alpha("#C4922A", 0.25) : alpha("#2563EB", 0.2)
                }`,
              }}
            />
          </Box>

          {/* ── Form Card ── */}
          <SectionCard delay={50}>
            <CardLabel>
              {editingExamId ? "Edit Exam" : "Add New Exam"}
            </CardLabel>

            {/* Exam Name */}
            <Box sx={{ mb: 2.5 }}>
              <FieldLabel>Exam Name *</FieldLabel>
              <StyledInput
                placeholder="e.g. JAMB 2024, WAEC 2025…"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Box>

            {/* Post-UTME Toggle */}
            <Box sx={{ mb: 2.5 }}>
              <FieldLabel>Exam Type</FieldLabel>
              <ToggleBox
                active={isPostUTME ? 1 : 0}
                onClick={() => setIsPostUTME((prev) => !prev)}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: isPostUTME ? "secondary.main" : "text.primary",
                      fontFamily: "'Sora', sans-serif",
                      lineHeight: 1.2,
                    }}
                  >
                    {isPostUTME ? "Post-UTME Exam" : "Regular Exam"}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "text.disabled",
                      fontFamily: "'Sora', sans-serif",
                      mt: 0.3,
                    }}
                  >
                    {isPostUTME
                      ? "This exam is classified as Post-UTME"
                      : "Click to mark as Post-UTME"}
                  </Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Chip
                    label={isPostUTME ? "Post-UTME" : "Regular"}
                    size="small"
                    sx={{
                      fontFamily: "'Sora', sans-serif",
                      fontWeight: 700,
                      fontSize: 10,
                      background: isPostUTME
                        ? alpha("#C4922A", 0.12)
                        : alpha("#000", 0.06),
                      color: isPostUTME ? "#C4922A" : "text.secondary",
                      border: `1px solid ${
                        isPostUTME ? alpha("#C4922A", 0.3) : alpha("#000", 0.1)
                      }`,
                    }}
                  />
                  <ToggleKnob active={isPostUTME ? 1 : 0} />
                </Stack>
              </ToggleBox>
            </Box>

            {/* Image Upload */}
            <Box sx={{ mb: 3 }}>
              <FieldLabel>Exam Image (optional)</FieldLabel>
              <ImageUploadZone
                hasimage={displayImage ? 1 : 0}
                onClick={() =>
                  document.getElementById("exam-img-input").click()
                }
              >
                <input
                  id="exam-img-input"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleImageChange}
                />
                {displayImage ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      component="img"
                      src={displayImage}
                      alt="Preview"
                      sx={{
                        maxHeight: 160,
                        maxWidth: "100%",
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        display: "block",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "text.disabled",
                        fontFamily: "'Sora', sans-serif",
                        mt: 0.5,
                      }}
                    >
                      Click to change image
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.6,
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 3,
                        background: alpha("#2563EB", 0.08),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 0.5,
                      }}
                    >
                      <ImageIcon
                        sx={{
                          fontSize: 22,
                          color: "primary.main",
                          opacity: 0.7,
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "text.primary",
                        fontFamily: "'Sora', sans-serif",
                      }}
                    >
                      Upload exam image
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "text.disabled",
                        fontFamily: "'Sora', sans-serif",
                      }}
                    >
                      PNG, JPG · max 10 MB
                    </Typography>
                  </Box>
                )}
              </ImageUploadZone>
            </Box>

            {/* Actions */}
            <Stack direction="row" spacing={1.5}>
              <PrimaryBtn
                startIcon={
                  editingExamId ? (
                    <CheckIcon sx={{ fontSize: "16px !important" }} />
                  ) : (
                    <AddIcon sx={{ fontSize: "16px !important" }} />
                  )
                }
                disabled={isLoading || !name.trim()}
                onClick={submitExam}
              >
                {isLoading
                  ? "Saving…"
                  : editingExamId
                  ? "Update Exam"
                  : "Create Exam"}
              </PrimaryBtn>
              {editingExamId && (
                <Button
                  onClick={resetForm}
                  sx={{
                    borderRadius: 2.5,
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: "none",
                    border: `1px solid ${alpha("#000", 0.12)}`,
                    color: "text.secondary",
                    px: 2.5,
                    fontFamily: "'Sora', sans-serif",
                    "&:hover": { background: alpha("#000", 0.04) },
                  }}
                >
                  Cancel
                </Button>
              )}
            </Stack>
          </SectionCard>

          {/* ── Exams Grid ── */}
          <SectionCard delay={120}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2.5,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <CardLabel sx={{ mb: 0 }}>All Exams</CardLabel>
              <Chip
                label={`${exams.length} exam${exams.length !== 1 ? "s" : ""}`}
                size="small"
                sx={{
                  fontFamily: "'Sora', sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                  background: alpha("#2563EB", 0.08),
                  color: "#2563EB",
                  border: `1px solid ${alpha("#2563EB", 0.18)}`,
                }}
              />
            </Box>

            {fetching ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 5,
                  gap: 2,
                }}
              >
                <Spinner />
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                  }}
                >
                  Loading exams…
                </Typography>
              </Box>
            ) : exams.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 3,
                    background: alpha("#2563EB", 0.07),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <QuizIcon
                    sx={{ fontSize: 26, color: "primary.main", opacity: 0.5 }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 600,
                  }}
                >
                  No exams created yet
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.4,
                  }}
                >
                  Use the form above to create your first exam
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2.5}>
                {exams.map((exam, i) => (
                  <Grid item xs={12} sm={6} md={4} key={exam.id}>
                    <ExamCard itemdelay={i * 55}>
                      {/* Image / Placeholder */}
                      <ExamImageBox>
                        {exam.exam_img ? (
                          <Box
                            component="img"
                            src={exam.exam_img}
                            alt={exam.name}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.8,
                              opacity: 0.4,
                            }}
                          >
                            <QuizIcon sx={{ fontSize: 32, color: "#2563EB" }} />
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: "#2563EB",
                                fontFamily: "'Sora', sans-serif",
                                letterSpacing: "0.5px",
                              }}
                            >
                              NO IMAGE
                            </Typography>
                          </Box>
                        )}
                        {/* Post-UTME ribbon */}
                        {exam.is_postUTME && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 10,
                              right: 10,
                              background:
                                "linear-gradient(135deg, #C4922A, #92680C)",
                              color: "#fff",
                              borderRadius: "6px",
                              padding: "3px 9px",
                              fontSize: 10,
                              fontWeight: 800,
                              fontFamily: "'Sora', sans-serif",
                              letterSpacing: "0.5px",
                              boxShadow: "0 2px 6px rgba(196,146,42,0.4)",
                            }}
                          >
                            POST-UTME
                          </Box>
                        )}
                      </ExamImageBox>

                      {/* Card body */}
                      <Box
                        sx={{
                          padding: "16px 18px",
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 15,
                            fontWeight: 700,
                            color: "text.primary",
                            fontFamily: "'DM Serif Display', serif",
                            mb: 0.5,
                            lineHeight: 1.25,
                          }}
                        >
                          {exam.name}
                        </Typography>

                        <Chip
                          label={
                            exam.is_postUTME ? "Post-UTME" : "Regular Exam"
                          }
                          size="small"
                          sx={{
                            alignSelf: "flex-start",
                            mb: 1.5,
                            fontFamily: "'Sora', sans-serif",
                            fontWeight: 700,
                            fontSize: 10,
                            background: exam.is_postUTME
                              ? alpha("#C4922A", 0.1)
                              : alpha("#2563EB", 0.07),
                            color: exam.is_postUTME ? "#C4922A" : "#2563EB",
                            border: `1px solid ${
                              exam.is_postUTME
                                ? alpha("#C4922A", 0.25)
                                : alpha("#2563EB", 0.18)
                            }`,
                          }}
                        />

                        <Box sx={{ flex: 1 }} />

                        {/* Action buttons */}
                        <Stack direction="row" spacing={0.8} sx={{ mt: 1 }}>
                          <Tooltip title="Manage Objective Subjects" arrow>
                            <NavActionBtn
                              btnvariant="objective"
                              onClick={() => navigateToSubject(exam.id)}
                            >
                              <MenuBookIcon
                                className="btn-icon"
                                sx={{
                                  fontSize: 13,
                                  color: "#2563EB",
                                  transition: "color 0.18s",
                                }}
                              />
                              <Typography
                                className="btn-label"
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "#2563EB",
                                  fontFamily: "'Sora', sans-serif",
                                  transition: "color 0.18s",
                                }}
                              >
                                Objective
                              </Typography>
                            </NavActionBtn>
                          </Tooltip>

                          <Tooltip title="Manage Theory Subjects" arrow>
                            <NavActionBtn
                              btnvariant="theory"
                              onClick={() => navigateToTheory(exam.id)}
                            >
                              <HistoryEduIcon
                                className="btn-icon"
                                sx={{
                                  fontSize: 13,
                                  color: "#059669",
                                  transition: "color 0.18s",
                                }}
                              />
                              <Typography
                                className="btn-label"
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "#059669",
                                  fontFamily: "'Sora', sans-serif",
                                  transition: "color 0.18s",
                                }}
                              >
                                Theory
                              </Typography>
                            </NavActionBtn>
                          </Tooltip>

                          <Tooltip title="Edit Exam" arrow>
                            <NavActionBtn
                              btnvariant="edit"
                              onClick={() => editExam(exam.id)}
                            >
                              <EditIcon
                                className="btn-icon"
                                sx={{
                                  fontSize: 13,
                                  color: "#C4922A",
                                  transition: "color 0.18s",
                                }}
                              />
                              <Typography
                                className="btn-label"
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "#C4922A",
                                  fontFamily: "'Sora', sans-serif",
                                  transition: "color 0.18s",
                                }}
                              >
                                Edit
                              </Typography>
                            </NavActionBtn>
                          </Tooltip>
                        </Stack>
                      </Box>
                    </ExamCard>
                  </Grid>
                ))}
              </Grid>
            )}
          </SectionCard>
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
}
